import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Package, 
  Save,
  X,
  Edit2
} from 'lucide-react';
import { Product } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface EditableProduct extends Product {
  editing?: {
    actualPrice?: boolean;
    markedPrice?: boolean;
    serviceCost?: boolean;
    marginCost?: boolean;
  };
  tempValues?: {
    actualPrice?: string;
    markedPrice?: string;
    serviceCost?: string;
    marginCost?: string;
  };
}

const ProductGridPage: React.FC = () => {
  const [products, setProducts] = useState<EditableProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadProducts();
  }, [currentPage, search]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await apiService.getProducts(currentPage, pageSize, search);
      if (response.isSuccess && response.entity) {
        const productsList = response.entity.items || response.entity || [];
        setProducts(productsList.map((p: any) => ({
          ...p,
          // Map marginalCost from API to marginCost
          marginCost: p.marginalCost ?? p.marginCost ?? 0,
          editing: {},
          tempValues: {}
        })));
        setTotalCount(response.entity.totalCount || 0);
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to load products');
        setProducts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Error connecting to server. Please check if the backend is running.');
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (productId: number, field: 'actualPrice' | 'markedPrice' | 'serviceCost' | 'marginCost') => {
    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        let currentValue: string;
        if (field === 'markedPrice') {
          currentValue = p.markedPrice;
        } else if (field === 'marginCost') {
          // Handle both marginCost and marginalCost
          currentValue = (p.marginCost ?? (p as any).marginalCost ?? 0).toString();
        } else {
          currentValue = p[field]?.toString() || '';
        }
        return {
          ...p,
          editing: { ...p.editing, [field]: true },
          tempValues: { ...p.tempValues, [field]: currentValue }
        };
      }
      return p;
    }));
  };

  const cancelEditing = (productId: number, field: 'actualPrice' | 'markedPrice' | 'serviceCost' | 'marginCost') => {
    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        const newEditing = { ...p.editing };
        delete newEditing[field];
        const newTempValues = { ...p.tempValues };
        delete newTempValues[field];
        return {
          ...p,
          editing: newEditing,
          tempValues: newTempValues
        };
      }
      return p;
    }));
  };

  const updateTempValue = (productId: number, field: 'actualPrice' | 'markedPrice' | 'serviceCost' | 'marginCost', value: string) => {
    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        return {
          ...p,
          tempValues: { ...p.tempValues, [field]: value }
        };
      }
      return p;
    }));
  };

  const saveField = async (product: EditableProduct, field: 'actualPrice' | 'markedPrice' | 'serviceCost' | 'marginCost') => {
    const tempValue = product.tempValues?.[field];
    if (tempValue === undefined) return;

    // Validate the value
    const numValue = field === 'markedPrice' ? parseFloat(tempValue) : parseFloat(tempValue);
    if (isNaN(numValue) || numValue < 0) {
      toast.error(`${field === 'markedPrice' ? 'Marked Price' : field === 'actualPrice' ? 'Parts Price' : field === 'serviceCost' ? 'Service Cost' : 'Margin Cost'} must be a valid number greater than or equal to 0`);
      cancelEditing(product.id, field);
      return;
    }

    // Check if value actually changed
    const currentValue = field === 'markedPrice' 
      ? parseFloat(product.markedPrice) 
      : (product[field] as number) || 0;
    
    if (numValue === currentValue) {
      cancelEditing(product.id, field);
      return;
    }

    setSaving(product.id);
    try {
      const formData = new FormData();
      formData.append('id', product.id.toString());
      formData.append('makeId', product.makeId.toString());
      formData.append('modelId', product.modelId.toString());
      formData.append('modelName', product.modelName || '');
      formData.append('serviceName', product.serviceName);
      formData.append('actualPrice', field === 'actualPrice' ? numValue.toString() : product.actualPrice.toString());
      formData.append('markedPrice', field === 'markedPrice' ? numValue.toString() : product.markedPrice);
      formData.append('serviceDuration', product.serviceDuration.toString());
      formData.append('serviceCost', field === 'serviceCost' ? numValue.toString() : product.serviceCost.toString());
      formData.append('MarginalCost', field === 'marginCost' ? numValue.toString() : (product.marginCost?.toString() || '0'));

      const response = await apiService.updateProduct(product.id, formData);
      if (response.isSuccess) {
        toast.success(`${field === 'markedPrice' ? 'Marked Price' : field === 'actualPrice' ? 'Parts Price' : field === 'serviceCost' ? 'Service Cost' : 'Margin Cost'} updated successfully`);
        
        // Update the product in the list
        setProducts(prev => prev.map(p => {
          if (p.id === product.id) {
            const updated: EditableProduct = {
              ...p,
              [field]: numValue,
              markedPrice: field === 'markedPrice' ? numValue.toString() : p.markedPrice,
              marginCost: field === 'marginCost' ? numValue : p.marginCost
            };
            const newEditing = { ...updated.editing };
            delete newEditing[field];
            const newTempValues = { ...updated.tempValues };
            delete newTempValues[field];
            return {
              ...updated,
              editing: newEditing,
              tempValues: newTempValues
            };
          }
          return p;
        }));
      } else {
        toast.error(response.errors?.join(', ') || `Failed to update ${field}`);
        cancelEditing(product.id, field);
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Error updating ${field}`);
      cancelEditing(product.id, field);
    } finally {
      setSaving(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, product: EditableProduct, field: 'actualPrice' | 'markedPrice' | 'serviceCost' | 'marginCost') => {
    if (e.key === 'Enter') {
      saveField(product, field);
    } else if (e.key === 'Escape') {
      cancelEditing(product.id, field);
    }
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
          <Package className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Product Grid</h1>
          <p className="text-sm text-gray-500">Edit product prices and costs inline</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Search Products</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by product name, make, or model..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="text-sm text-gray-500">
            Showing {products.length} of {totalCount} products
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-500">Try adjusting your search criteria.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Make / Model
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Parts Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Marked Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Margin Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 group">
                      {/* Make / Model */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{product.make}</div>
                        <div className="text-xs text-gray-500">{product.modelName}</div>
                      </td>

                      {/* Product Name */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {product.serviceImage && (
                            <img
                              src={apiService.getFullImageUrl(product.serviceImage)}
                              alt={product.serviceName}
                              className="h-10 w-10 rounded-lg object-cover mr-3"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{product.serviceName}</div>
                            <div className="text-xs text-gray-500">{product.serviceType}</div>
                          </div>
                        </div>
                      </td>

                      {/* Parts Price */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.editing?.actualPrice ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={product.tempValues?.actualPrice || ''}
                              onChange={(e) => updateTempValue(product.id, 'actualPrice', e.target.value)}
                              onBlur={() => saveField(product, 'actualPrice')}
                              onKeyDown={(e) => handleKeyDown(e, product, 'actualPrice')}
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                              autoFocus
                              disabled={saving === product.id}
                            />
                            <button
                              onClick={() => saveField(product, 'actualPrice')}
                              disabled={saving === product.id}
                              className="text-green-600 hover:text-green-800 disabled:opacity-50"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => cancelEditing(product.id, 'actualPrice')}
                              disabled={saving === product.id}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(product.actualPrice)}
                            </span>
                            {saving !== product.id && (
                              <button
                                onClick={() => startEditing(product.id, 'actualPrice')}
                                className="text-purple-600 hover:text-purple-800 transition-colors"
                                title="Edit Parts Price"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            )}
                            {saving === product.id && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Marked Price */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.editing?.markedPrice ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={product.tempValues?.markedPrice || ''}
                              onChange={(e) => updateTempValue(product.id, 'markedPrice', e.target.value)}
                              onBlur={() => saveField(product, 'markedPrice')}
                              onKeyDown={(e) => handleKeyDown(e, product, 'markedPrice')}
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                              autoFocus
                              disabled={saving === product.id}
                            />
                            <button
                              onClick={() => saveField(product, 'markedPrice')}
                              disabled={saving === product.id}
                              className="text-green-600 hover:text-green-800 disabled:opacity-50"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => cancelEditing(product.id, 'markedPrice')}
                              disabled={saving === product.id}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(product.markedPrice)}
                            </span>
                            {saving !== product.id && (
                              <button
                                onClick={() => startEditing(product.id, 'markedPrice')}
                                className="text-purple-600 hover:text-purple-800 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit Marked Price"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            )}
                            {saving === product.id && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Service Cost */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.editing?.serviceCost ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={product.tempValues?.serviceCost || ''}
                              onChange={(e) => updateTempValue(product.id, 'serviceCost', e.target.value)}
                              onBlur={() => saveField(product, 'serviceCost')}
                              onKeyDown={(e) => handleKeyDown(e, product, 'serviceCost')}
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                              autoFocus
                              disabled={saving === product.id}
                            />
                            <button
                              onClick={() => saveField(product, 'serviceCost')}
                              disabled={saving === product.id}
                              className="text-green-600 hover:text-green-800 disabled:opacity-50"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => cancelEditing(product.id, 'serviceCost')}
                              disabled={saving === product.id}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(product.serviceCost)}
                            </span>
                            {saving !== product.id && (
                              <button
                                onClick={() => startEditing(product.id, 'serviceCost')}
                                className="text-purple-600 hover:text-purple-800 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit Service Cost"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            )}
                            {saving === product.id && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Margin Cost */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.editing?.marginCost ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={product.tempValues?.marginCost || ''}
                              onChange={(e) => updateTempValue(product.id, 'marginCost', e.target.value)}
                              onBlur={() => saveField(product, 'marginCost')}
                              onKeyDown={(e) => handleKeyDown(e, product, 'marginCost')}
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                              autoFocus
                              disabled={saving === product.id}
                            />
                            <button
                              onClick={() => saveField(product, 'marginCost')}
                              disabled={saving === product.id}
                              className="text-green-600 hover:text-green-800 disabled:opacity-50"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => cancelEditing(product.id, 'marginCost')}
                              disabled={saving === product.id}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(product.marginCost ?? (product as any).marginalCost ?? 0)}
                            </span>
                            {saving !== product.id && (
                              <button
                                onClick={() => startEditing(product.id, 'marginCost')}
                                className="text-purple-600 hover:text-purple-800 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit Margin Cost"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            )}
                            {saving === product.id && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && products.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage <= 1}
                      className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = Math.max(1, currentPage - 2) + i;
                      if (page > totalPages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 text-sm font-medium rounded-md ${
                            page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductGridPage;

